import clsx from 'clsx';

import { Avatar, Chip, Icon, IconButton, Link, Paper, Typography } from '@/ui';

import { formatIsoTimeRange } from './calendarGrid';
import { deriveSessionStatus } from './sessionData';

import type { Session } from '@/types';
import type { FC, MouseEvent } from 'react';

export interface ScheduleSessionCardProps {
  hasOptions: boolean;
  menuId: string;
  menuOpen: boolean;
  profileMemberId?: string;
  session: Session;
  timeZone?: string | null;
  onOpenMenu: (session: Session, anchor: HTMLElement) => void;
}

// Coach Schedule list card (MEN-4160). Prioritizes the MEMBER as the headline
// with their company as the subtitle, per the design.
const ScheduleSessionCard: FC<ScheduleSessionCardProps> = ({
  hasOptions,
  menuId,
  menuOpen,
  onOpenMenu,
  profileMemberId,
  session,
  timeZone,
}) => {
  const member = session.user;
  const status = deriveSessionStatus(session);
  const isCancelled = status?.tone === 'cancelled';
  const cancellationAttribution =
    isCancelled && status.label !== 'Cancelled' ? status.label : null;
  const isPast = Date.parse(session.endedAt) < Date.now();

  const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onOpenMenu(session, event.currentTarget);
  };

  return (
    <Paper
      className={clsx(
        'flex min-h-24 w-full items-center gap-5 rounded-xl border border-cement-200 px-6 py-4',
        // MEN-5355: cancelled wins over the past fill so it scans from the left.
        isCancelled && 'bg-bloom-200/50',
        !isCancelled && (isPast ? 'bg-cement-100' : 'bg-white'),
      )}
      data-testid="schedule-session-card"
      elevation={1}
    >
      <Link
        aria-label={`View profile for ${member.fullName}`}
        className="flex min-w-0 flex-1 items-center gap-4 text-cement-500 no-underline hover:text-cement-500"
        href={`/coach/members/${encodeURIComponent(profileMemberId ?? member.id)}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        <Avatar
          firstName={member.firstName}
          lastName={member.lastName}
          src={member.avatarUrl}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Typography
              className={isCancelled ? 'line-through' : undefined}
              variant="subtitle1"
            >
              {member.fullName}
            </Typography>
            {member.company?.name && (
              <Typography
                className={isCancelled ? 'line-through' : undefined}
                color="textSecondary"
                variant="caption"
              >
                {member.company.name}
              </Typography>
            )}
          </div>
          <Typography
            className={isCancelled ? 'line-through' : undefined}
            color="textSecondary"
            variant="body2"
          >
            {`${formatIsoTimeRange(session.startedAt, session.endedAt, timeZone)} • ${session.title}`}
          </Typography>
        </div>
      </Link>

      {cancellationAttribution && (
        <Typography className="text-cement-300" variant="caption">
          {cancellationAttribution}
        </Typography>
      )}

      {status && (
        <Chip
          color={isCancelled ? 'info' : 'success'}
          data-testid="schedule-session-status"
          label={isCancelled ? 'Cancelled' : status.label}
          size="small"
        />
      )}

      {hasOptions && (
        <IconButton
          aria-controls={menuOpen ? menuId : undefined}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label={`Session options for ${member.fullName}`}
          onClick={handleMenu}
        >
          <Icon>more_horiz</Icon>
        </IconButton>
      )}
    </Paper>
  );
};

export default ScheduleSessionCard;
